import { useState, useEffect, useCallback, useMemo, useRef } from 'preact/hooks'
import type { ComponentChildren } from 'preact'
import type { Persona, CloudTask, CloudNote, ChatSession } from '../types'
import { PERSONA_INFO } from '../types'
import { listCloudTasks, listCloudNotes, listCloudSessions } from '../services/api'
import { loadCardPrefs, saveCardPrefs, CARD_ITEM_CAP } from '../services/homeLayout'
import type { CardId, CardPref } from '../services/homeLayout'
import {
  selectTodayTasks, selectTodayEvents, selectTodayReminders, selectRecentNotes, selectRecentChats,
  createTask, createEvent, createReminder, createNote, completeTask, restoreTask,
  rescheduleTask, rescheduleReminder, cancelReminder, restoreNote, taskWhen, reminderWhen,
  toDateInput, toTimeInput, fromInputs, nextRoundHour, formatTime, formatDateTimeFi, sameLocalDay,
} from '../services/homeItems'
import type { HomeRow, TaskDraft, EventDraft, ReminderDraft, NoteDraft } from '../services/homeItems'
import { parseQuickLine, looksConversational, CONFIDENCE_THRESHOLD } from '../services/quickLine'
import type { QuickIntent, ContextItem } from '../services/quickLine'
import { FormField } from './views/CrudHelpers'
import { askQuickChat, loadQuickChats } from '../services/quickChat'
import type { QuickChatEntry } from '../services/quickChat'

/**
 * State-first home screen prototype (pipar-home-ui-plan.md).
 * Answers "what's on my plate today" with zero taps; conversation is how you
 * change it. Lives alongside the classic LandingPage behind a layout switch.
 */

interface HomeScreenProps {
  personas: Persona[]
  selectedPersona: Persona
  personaImages: Record<string, string>
  onPersonaChange: (persona: Persona) => void
  onNavigate: (view: string, focusId?: string) => void
  /** Open the chat thread with the selected persona, optionally sending text */
  onOpenChat: (text?: string) => void
  /** Continue a quick answer in full chat, carrying the exchange as history */
  onContinueQuickChat: (entry: QuickChatEntry) => void
  onOpenSession: (sessionId: string) => void
  onUseClassic: () => void
}

const CARD_META: Record<CardId, { title: string; view: string }> = {
  today: { title: 'Tänään', view: 'tasks' },
  calendar: { title: 'Kalenteri', view: 'calendar' },
  reminders: { title: 'Muistutukset', view: 'calls' },
  quick: { title: 'Pikavastaukset', view: '' }, // header opens the answers sheet, not a view
  notes: { title: 'Muistiinpanot', view: 'notes' },
  chats: { title: 'Keskustelut', view: 'chat' },
}

const AMBER = '#F5A623'
const DRAFT_KEY = 'pipar_home_draft'

type Sheet =
  | { kind: 'task'; task: CloudTask }
  | { kind: 'event'; task: CloudTask }
  | { kind: 'reminder'; note: CloudNote }
  | { kind: 'newTask'; draft: TaskDraft }
  | { kind: 'newEvent'; draft: EventDraft }
  | { kind: 'newReminder'; draft: ReminderDraft }
  | { kind: 'newNote'; draft: NoteDraft }
  | { kind: 'persona' }
  | { kind: 'quickList'; focusId?: string }

type CreateIntent = Extract<QuickIntent, { kind: 'create' }>
type UpdateIntent = Extract<QuickIntent, { kind: 'update' }>

type Quick =
  | { status: 'idle' }
  | { status: 'parsing'; text: string }
  | { status: 'confirm'; text: string; intent: CreateIntent | UpdateIntent }
  | { status: 'answer'; entryId: string }

interface Snack { message: string; undo?: () => Promise<void> }

function readDraft(): string {
  try { return sessionStorage.getItem(DRAFT_KEY) || '' } catch { return '' }
}

function hasClockTime(d: Date): boolean {
  return d.getHours() !== 0 || d.getMinutes() !== 0
}

export function HomeScreen({
  personas, selectedPersona, personaImages, onPersonaChange, onNavigate, onOpenChat, onContinueQuickChat,
  onOpenSession, onUseClassic,
}: HomeScreenProps) {
  const [tasks, setTasks] = useState<CloudTask[]>([])
  const [notes, setNotes] = useState<CloudNote[]>([])
  const [sessions, setSessions] = useState<ChatSession[]>([])
  const [loaded, setLoaded] = useState(false)
  const [cardPrefs, setCardPrefs] = useState<CardPref[]>(loadCardPrefs)
  const [sheet, setSheet] = useState<Sheet | null>(null)
  const [quick, setQuick] = useState<Quick>({ status: 'idle' })
  const [snack, setSnack] = useState<Snack | null>(null)
  const [draft, setDraftState] = useState(readDraft)
  const [quickChats, setQuickChats] = useState<QuickChatEntry[]>(loadQuickChats)
  const snackTimer = useRef<number | undefined>(undefined)

  const setDraft = useCallback((text: string) => {
    setDraftState(text)
    try { sessionStorage.setItem(DRAFT_KEY, text) } catch { /* storage unavailable */ }
  }, [])

  const refresh = useCallback(async () => {
    const [t, n, s] = await Promise.allSettled([listCloudTasks(), listCloudNotes(), listCloudSessions(undefined, 10)])
    if (t.status === 'fulfilled') setTasks(t.value); else console.warn('[home] tasks failed', t.reason)
    if (n.status === 'fulfilled') setNotes(n.value); else console.warn('[home] notes failed', n.reason)
    if (s.status === 'fulfilled') setSessions(s.value.sessions || []); else console.warn('[home] sessions failed', s.reason)
    setLoaded(true)
  }, [])

  useEffect(() => { refresh() }, [refresh])

  const showSnack = useCallback((next: Snack) => {
    window.clearTimeout(snackTimer.current)
    setSnack(next)
    snackTimer.current = window.setTimeout(() => setSnack(null), 5000)
  }, [])
  useEffect(() => () => window.clearTimeout(snackTimer.current), [])

  const rows = useMemo(() => {
    const now = new Date()
    return {
      today: selectTodayTasks(tasks, now),
      calendar: selectTodayEvents(tasks, now),
      reminders: selectTodayReminders(notes, now),
      notes: selectRecentNotes(notes),
      chats: selectRecentChats(sessions),
      quick: quickChats.map((e) => ({ id: e.id, title: e.question, when: new Date(e.createdAt) })),
    } satisfies Record<CardId, HomeRow[]>
  }, [tasks, notes, sessions, quickChats])

  /** Structured block of what's in view, for resolving "siirrä se perjantaille". */
  const contextItems = useMemo<ContextItem[]>(() => {
    const toItem = (kind: ContextItem['kind']) => (r: HomeRow): ContextItem => ({
      id: r.id, kind, title: r.title, when: r.when ? r.when.toISOString() : undefined,
    })
    return [
      ...rows.today.slice(0, 10).map(toItem('task')),
      ...rows.calendar.slice(0, 10).map(toItem('event')),
      ...rows.reminders.slice(0, 10).map(toItem('reminder')),
    ]
  }, [rows])

  const updateCardState = (id: CardId, state: CardPref['state']) => {
    const next = cardPrefs.map((c) => (c.card_id === id ? { ...c, state } : c))
    setCardPrefs(next)
    saveCardPrefs(next)
  }

  // ── Actions (all writes go through services/homeItems) ──────────

  const runWrite = async (fn: () => Promise<unknown>, message: string, undo?: () => Promise<void>) => {
    try {
      await fn()
      showSnack({ message, undo })
    } catch (e) {
      console.error('[home] write failed', e)
      showSnack({ message: `Tallennus epäonnistui: ${e instanceof Error ? e.message : String(e)}` })
    }
    refresh()
  }

  const handleComplete = (task: CloudTask) => {
    // Optimistic: drop the row immediately, reconcile on refresh.
    setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, status: 2 } : t)))
    setSheet(null)
    runWrite(() => completeTask(task), `Tehty: ${task.title}`, async () => {
      await restoreTask(task)
      refresh()
    })
  }

  const handleRescheduleTask = (task: CloudTask, at: Date) => {
    setSheet(null)
    runWrite(() => rescheduleTask(task, at), `Siirretty: ${formatDateTimeFi(at)}`, async () => {
      await restoreTask(task)
      refresh()
    })
  }

  const handleRescheduleReminder = (note: CloudNote, at: Date) => {
    setSheet(null)
    runWrite(() => rescheduleReminder(note, at), `Siirretty: ${formatDateTimeFi(at)}`, async () => {
      await restoreNote(note)
      refresh()
    })
  }

  const handleCancelReminder = (note: CloudNote) => {
    setSheet(null)
    runWrite(() => cancelReminder(note), 'Muistutus peruttu', async () => {
      await restoreNote(note)
      refresh()
    })
  }

  const openAdd = (id: CardId) => {
    const now = new Date()
    const slot = nextRoundHour(now)
    switch (id) {
      case 'today':
        setSheet({ kind: 'newTask', draft: { title: '', date: toDateInput(now), time: '' } })
        break
      case 'calendar':
        setSheet({ kind: 'newEvent', draft: { title: '', date: toDateInput(slot), time: toTimeInput(slot), durationMin: 60, location: '' } })
        break
      case 'reminders':
        setSheet({ kind: 'newReminder', draft: { title: '', date: toDateInput(slot), time: toTimeInput(slot), personaId: selectedPersona.id } })
        break
      case 'notes':
        setSheet({ kind: 'newNote', draft: { title: '', content: '' } })
        break
      case 'chats':
        onOpenChat()
        break
    }
  }

  const openRow = (card: CardId, id: string) => {
    if (card === 'today' || card === 'calendar') {
      const task = tasks.find((t) => t.id === id)
      if (task) setSheet({ kind: card === 'today' ? 'task' : 'event', task })
    } else if (card === 'reminders') {
      const note = notes.find((n) => n.id === id)
      if (note) setSheet({ kind: 'reminder', note })
    } else if (card === 'quick') {
      setSheet({ kind: 'quickList', focusId: id })
    } else if (card === 'notes') {
      onNavigate('notes', id)
    } else {
      onOpenSession(id)
    }
  }

  /** Open the detail sheet for whatever an id in the context block refers to. */
  const openTarget = (id: string | null): boolean => {
    if (!id) return false
    const task = tasks.find((t) => t.id === id)
    if (task) { setSheet({ kind: task.type === 1 ? 'event' : 'task', task }); return true }
    const note = notes.find((n) => n.id === id)
    if (note) { setSheet({ kind: 'reminder', note }); return true }
    return false
  }

  const openCreateSheet = (intent: CreateIntent) => {
    const at = intent.datetime
    const slot = nextRoundHour()
    const date = toDateInput(at || new Date())
    const time = at && intent.hasTime ? toTimeInput(at) : ''
    if (intent.entity === 'task') {
      setSheet({ kind: 'newTask', draft: { title: intent.title, date, time } })
    } else if (intent.entity === 'event') {
      setSheet({ kind: 'newEvent', draft: { title: intent.title, date: at ? date : toDateInput(slot), time: time || toTimeInput(slot), durationMin: 60, location: '' } })
    } else {
      setSheet({ kind: 'newReminder', draft: { title: intent.title, date: at ? date : toDateInput(slot), time: time || toTimeInput(slot), personaId: selectedPersona.id } })
    }
  }

  // ── Quick line ─────────────────────────────────────────────────

  /** Answer a question in place on the home screen (result card, not a chat bubble). */
  const startQuickAnswer = (text: string) => {
    let shownId: string | null = null
    askQuickChat(text, (entries) => {
      setQuickChats(entries)
      if (!shownId && entries[0]) {
        shownId = entries[0].id
        setQuick({ status: 'answer', entryId: shownId })
      }
    }).then((entry) => {
      // A tool changed the user's data (e.g. created a task) — reflect it in the cards.
      if (entry.changedData) refresh()
    })
  }

  const routeIntent = (text: string, intent: QuickIntent) => {
    switch (intent.kind) {
      case 'chat':
      case 'unknown':
        // Never show a bare "en ymmärtänyt" — answer it as a quick question.
        startQuickAnswer(text)
        return
      case 'delete':
        // Delete is never one-shot: open the item's sheet, or hand over to chat.
        setQuick({ status: 'idle' })
        if (!openTarget(intent.targetId)) onOpenChat(text)
        return
      case 'complete': {
        setQuick({ status: 'idle' })
        const task = tasks.find((t) => t.id === intent.targetId)
        if (task && task.type !== 1 && intent.confidence >= CONFIDENCE_THRESHOLD) handleComplete(task)
        else if (!openTarget(intent.targetId)) onOpenChat(text)
        return
      }
      case 'update':
        if (intent.datetime && intent.confidence >= CONFIDENCE_THRESHOLD) {
          setQuick({ status: 'confirm', text, intent })
        } else {
          setQuick({ status: 'idle' })
          if (!openTarget(intent.targetId)) onOpenChat(text)
        }
        return
      case 'create':
        if (intent.confidence >= CONFIDENCE_THRESHOLD && intent.missing.length === 0) {
          setQuick({ status: 'confirm', text, intent })
        } else {
          // Low confidence or missing field: deterministic recovery via prefilled sheet.
          setQuick({ status: 'idle' })
          openCreateSheet(intent)
        }
    }
  }

  const submitQuick = async () => {
    const text = draft.trim()
    if (!text || quick.status === 'parsing') return
    setDraft('')
    // Obvious questions skip the tool-model parse and go straight to a quick answer.
    if (looksConversational(text)) {
      startQuickAnswer(text)
      return
    }
    setQuick({ status: 'parsing', text })
    let intent: QuickIntent
    try {
      intent = await parseQuickLine(text, contextItems)
    } catch (e) {
      console.warn('[home] quick line parse failed', e)
      intent = { kind: 'unknown' }
    }
    routeIntent(text, intent)
  }

  const confirmQuick = () => {
    if (quick.status !== 'confirm') return
    const { intent } = quick
    setQuick({ status: 'idle' })
    if (intent.kind === 'update') {
      const task = tasks.find((t) => t.id === intent.targetId)
      const note = notes.find((n) => n.id === intent.targetId)
      if (task && intent.datetime) handleRescheduleTask(task, intent.datetime)
      else if (note && intent.datetime) handleRescheduleReminder(note, intent.datetime)
      return
    }
    const at = intent.datetime
    const date = toDateInput(at || new Date())
    const time = at && intent.hasTime ? toTimeInput(at) : ''
    if (intent.entity === 'task') {
      runWrite(() => createTask({ title: intent.title, date, time }), `Tehtävä lisätty: ${intent.title}`)
    } else if (intent.entity === 'event') {
      runWrite(() => createEvent({ title: intent.title, date, time: time || '09:00', durationMin: 60, location: '' }), `Tapahtuma lisätty: ${intent.title}`)
    } else {
      runWrite(() => createReminder({ title: intent.title, date, time: time || '09:00', personaId: selectedPersona.id }), `Soitto ajastettu: ${intent.title}`)
    }
  }

  const editQuick = () => {
    if (quick.status !== 'confirm') return
    const { intent } = quick
    setQuick({ status: 'idle' })
    if (intent.kind === 'create') openCreateSheet(intent)
    else openTarget(intent.targetId)
  }

  const undoQuick = () => {
    if (quick.status !== 'parsing' && quick.status !== 'confirm') return
    setDraft(quick.text)
    setQuick({ status: 'idle' })
  }

  // ── Input bar actions ──────────────────────────────────────────

  const handleChatButton = () => {
    const text = draft.trim()
    setDraft('')
    onOpenChat(text || undefined)
  }

  const handleCallButton = () => {
    const slot = nextRoundHour()
    const text = draft.trim()
    setDraft('')
    setSheet({ kind: 'newReminder', draft: { title: text || 'Soita minulle', date: toDateInput(slot), time: toTimeInput(slot), personaId: selectedPersona.id } })
  }

  // ── Render ─────────────────────────────────────────────────────

  const visibleCards = cardPrefs.filter((c) => c.state !== 'hidden')
  const info = PERSONA_INFO[selectedPersona.id]

  return (
    <div style={s.outer}>
      <div style={s.column}>
        {/* Header */}
        <div style={s.header}>
          <button style={s.personaBtn} onClick={() => setSheet({ kind: 'persona' })} aria-label="Vaihda persoonaa">
            <Avatar persona={selectedPersona} url={personaImages[selectedPersona.id]} size={40} />
            <div style={{ minWidth: 0, textAlign: 'left' }}>
              <div style={s.personaName}>{selectedPersona.name}</div>
              <div style={s.tagline}>{info?.role || ''}</div>
            </div>
            <span style={{ color: 'var(--text-muted)', display: 'flex' }}><ChevronIcon /></span>
          </button>
          <div style={{ flex: 1 }} />
          <IconButton label="Sähköposti" onClick={() => onNavigate('email')}><MailIcon /></IconButton>
          <IconButton label="Asetukset" onClick={() => onNavigate('settings')}><GearIcon /></IconButton>
        </div>

        {/* Scroll region: quick-line result, cards, destinations */}
        <div style={s.scroll}>
          {quick.status === 'parsing' && (
            <div style={{ ...s.card, ...s.quickCard }}>
              <div style={s.quickLabel}>Tulkitaan…</div>
              <div style={s.quickText}>“{quick.text}”</div>
              <div style={s.quickActions}>
                <button style={s.textBtn} onClick={undoQuick}>Peru</button>
              </div>
            </div>
          )}
          {quick.status === 'confirm' && (
            <ConfirmCard
              quick={quick}
              tasks={tasks}
              notes={notes}
              personaName={selectedPersona.name}
              onConfirm={confirmQuick}
              onEdit={editQuick}
              onDismiss={undoQuick}
            />
          )}

          {visibleCards.map((pref) => {
            const id = pref.card_id
            const cardRows = rows[id]
            const empty = cardRows.length === 0
            // Empty cards auto-collapse; no empty-state illustrations.
            const expanded = pref.state === 'expanded' && !empty
            return (
              <section key={id} style={s.card}>
                <div style={s.cardHeader}>
                  <button style={s.cardTitleBtn} onClick={() => (id === 'chats' ? onOpenChat() : id === 'quick' ? setSheet({ kind: 'quickList' }) : onNavigate(CARD_META[id].view))}>
                    <span style={s.cardTitle}>{CARD_META[id].title}</span>
                    <span style={{ ...s.count, ...(empty ? { opacity: 0.5 } : {}) }}>
                      {loaded ? cardRows.length : '…'}
                    </span>
                  </button>
                  {!empty && (
                    <button
                      style={s.smallIconBtn}
                      aria-label={pref.state === 'expanded' ? 'Pienennä' : 'Laajenna'}
                      onClick={() => updateCardState(id, pref.state === 'expanded' ? 'collapsed' : 'expanded')}
                    >
                      <span style={{ display: 'flex', transform: pref.state === 'expanded' ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}><ChevronIcon /></span>
                    </button>
                  )}
                  {id !== 'quick' && (
                    <button style={s.smallIconBtn} aria-label="Lisää" onClick={() => openAdd(id)}>+</button>
                  )}
                </div>
                {expanded && cardRows.slice(0, CARD_ITEM_CAP).map((row) => (
                  <Row
                    key={row.id}
                    card={id}
                    row={row}
                    onOpen={() => openRow(id, row.id)}
                    onComplete={id === 'today' ? () => {
                      const task = tasks.find((t) => t.id === row.id)
                      if (task) handleComplete(task)
                    } : undefined}
                  />
                ))}
              </section>
            )
          })}

          {/* Destinations */}
          <div style={s.destinations}>
            <button style={s.destBtn} onClick={() => onNavigate('notes')}>
              <NoteIcon />
              <span>Muistiinpanot</span>
            </button>
            <button style={s.destBtn} onClick={() => onNavigate('apps')}>
              <SparkIcon />
              <span>Luomukset</span>
            </button>
          </div>

          <div style={{ textAlign: 'center', padding: '4px 0 12px' }}>
            <button style={s.textBtn} onClick={onUseClassic}>Vanha etusivu</button>
          </div>
        </div>

        {/* Snackbar */}
        {snack && (
          <div style={s.snack} role="status">
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{snack.message}</span>
            {snack.undo && (
              <button
                style={{ ...s.textBtn, color: 'var(--primary)', fontWeight: 600 }}
                onClick={() => { const u = snack.undo!; setSnack(null); u() }}
              >
                Kumoa
              </button>
            )}
          </div>
        )}

        <InputBar
          draft={draft}
          onDraft={setDraft}
          onSubmit={submitQuick}
          busy={quick.status === 'parsing'}
          onChat={handleChatButton}
          onCall={handleCallButton}
        />

        {quick.status === 'answer' && (
          <QuickAnswerDialog
            entry={quickChats.find((e) => e.id === quick.entryId)}
            onClose={() => setQuick({ status: 'idle' })}
            onShowAll={() => { setSheet({ kind: 'quickList', focusId: quick.entryId }); setQuick({ status: 'idle' }) }}
            onContinue={(entry) => { setQuick({ status: 'idle' }); onContinueQuickChat(entry) }}
          />
        )}
        {sheet?.kind === 'quickList' && (
          <QuickListSheet
            entries={quickChats}
            focusId={sheet.focusId}
            onClose={() => setSheet(null)}
            onContinue={(entry) => { setSheet(null); onContinueQuickChat(entry) }}
          />
        )}
        {sheet && sheet.kind !== 'quickList' && (
          <SheetHost
            sheet={sheet}
            personas={personas}
            selectedPersona={selectedPersona}
            personaImages={personaImages}
            onClose={() => setSheet(null)}
            onPersona={(p) => { onPersonaChange(p); setSheet(null) }}
            onComplete={handleComplete}
            onRescheduleTask={handleRescheduleTask}
            onRescheduleReminder={handleRescheduleReminder}
            onCancelReminder={handleCancelReminder}
            onNavigate={onNavigate}
            onCreate={(fn, message) => { setSheet(null); runWrite(fn, message) }}
          />
        )}
      </div>
    </div>
  )
}

// ── Confirmation card ────────────────────────────────────────────

const ENTITY_LABEL = { task: 'Uusi tehtävä', event: 'Uusi tapahtuma', reminder: 'Ajastettu soitto' } as const

function ConfirmCard({ quick, tasks, notes, personaName, onConfirm, onEdit, onDismiss }: {
  quick: Extract<Quick, { status: 'confirm' }>
  tasks: CloudTask[]
  notes: CloudNote[]
  personaName: string
  onConfirm: () => void
  onEdit: () => void
  onDismiss: () => void
}) {
  const { intent } = quick
  let label: string
  let title: string
  let when: string
  if (intent.kind === 'create') {
    label = ENTITY_LABEL[intent.entity]
    title = intent.title
    when = intent.datetime ? formatDateTimeFi(intent.datetime, intent.hasTime) : 'Ei päivämäärää'
    if (intent.entity === 'reminder') when += ` · ${personaName} soittaa`
  } else {
    const task = tasks.find((t) => t.id === intent.targetId)
    const note = notes.find((n) => n.id === intent.targetId)
    const prev = task ? taskWhen(task) : note ? reminderWhen(note) : null
    label = 'Siirrä'
    title = task?.title || note?.title || 'Kohde'
    when = `${prev ? formatDateTimeFi(prev) : '—'} → ${intent.datetime ? formatDateTimeFi(intent.datetime) : '—'}`
  }
  return (
    <div style={{ ...s.card, ...s.quickCard }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ ...s.quickLabel, flex: 1 }}>{label}</div>
        <button style={s.smallIconBtn} aria-label="Sulje" onClick={onDismiss}>{'×'}</button>
      </div>
      <div style={s.confirmTitle}>{title}</div>
      <div style={s.confirmWhen}>{when}</div>
      <div style={s.quickText}>“{quick.text}”</div>
      <div style={s.quickActions}>
        <button style={s.secondaryBtn} onClick={onEdit}>Muokkaa</button>
        <button style={s.primaryBtn} onClick={onConfirm}>Vahvista</button>
      </div>
    </div>
  )
}

// ── Card row ─────────────────────────────────────────────────────

// ── Quick answers ────────────────────────────────────────────────

/** Pop-up for a quick answer: opens on send and streams the answer in, over the cards. */
function QuickAnswerDialog({ entry, onClose, onShowAll, onContinue }: {
  entry?: QuickChatEntry
  onClose: () => void
  onShowAll: () => void
  onContinue: (entry: QuickChatEntry) => void
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  if (!entry) return null
  const pending = entry.status === 'pending'
  const label = pending
    ? entry.tools[entry.tools.length - 1] || 'Kirjoittaa…'
    : entry.status === 'error' ? 'Vastaus epäonnistui' : 'Vastaus'
  return (
    <div style={s.dialogOverlay} onClick={onClose}>
      <div style={s.dialog} role="dialog" aria-modal="true" aria-label="Vastaus" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ ...s.quickLabel, flex: 1 }}>{label}</div>
          <button style={s.smallIconBtn} aria-label="Sulje" onClick={onClose}>{'×'}</button>
        </div>
        <div style={s.dialogQuestion}>{entry.question}</div>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {entry.answer.trim()
            ? <AnswerText text={entry.answer} />
            : pending && <div style={{ ...s.quickText, marginTop: 10 }}>Odotetaan vastausta…</div>}
          {entry.status === 'error' && <div style={{ ...s.confirmWhen, color: AMBER, marginTop: 8 }}>{entry.error}</div>}
          {entry.changedData && !pending && <div style={s.quickText}>Tietojasi päivitettiin.</div>}
        </div>
        <div style={s.quickActions}>
          <button style={s.textBtn} onClick={onShowAll}>Kaikki vastaukset</button>
          <button style={s.secondaryBtn} onClick={() => onContinue(entry)}>Jatka keskustelua</button>
        </div>
      </div>
    </div>
  )
}

/** Minimal markdown for answers: **bold**, bullets and headings; everything else is plain text. */
export function AnswerText({ text }: { text: string }) {
  const lines = text.trim().split('\n').filter((line, i, all) => line.trim() || all[i - 1]?.trim())
  return (
    <div style={s.answerText}>
      {lines.map((line, i) => {
        if (!line.trim()) return <div key={i} style={{ height: 8 }} />
        const bullet = /^\s*[-*•]\s+/.test(line)
        const content = line.replace(/^\s*[-*•]\s+/, '').replace(/^#{1,6}\s+/, '')
        const parts = content.split(/(\*\*[^*]+\*\*)/g).map((part, j) =>
          part.length > 4 && part.startsWith('**') && part.endsWith('**') ? <strong key={j}>{part.slice(2, -2)}</strong> : part,
        )
        return (
          <div key={i} style={bullet ? { display: 'flex', gap: 8 } : undefined}>
            {bullet && <span style={{ color: 'var(--text-muted)' }}>•</span>}
            <span>{parts}</span>
          </div>
        )
      })}
    </div>
  )
}

function stripMarkdown(text: string): string {
  return text.replace(/\*\*/g, '').replace(/^#{1,6}\s+/gm, '').trim()
}

function QuickListSheet({ entries, focusId, onClose, onContinue }: {
  entries: QuickChatEntry[]
  focusId?: string
  onClose: () => void
  onContinue: (entry: QuickChatEntry) => void
}) {
  const [openId, setOpenId] = useState<string | null>(focusId ?? entries[0]?.id ?? null)
  const now = new Date()
  return (
    <BottomSheet title="Pikavastaukset" onClose={onClose}>
      {entries.length === 0 && (
        <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>Ei vielä kysymyksiä. Kirjoita kysymys alareunan kenttään.</div>
      )}
      {entries.map((e) => {
        const open = e.id === openId
        const when = new Date(e.createdAt)
        const body = e.answer || (e.status === 'pending' ? 'Kirjoittaa…' : e.error || '')
        return (
          <div key={e.id} style={s.quickItem}>
            <button style={s.quickItemHead} onClick={() => setOpenId(open ? null : e.id)}>
              <span style={s.rowTitle}>{e.question}</span>
              <span style={{ ...s.trailing, ...(e.status === 'error' ? { color: AMBER } : {}) }}>
                {sameLocalDay(when, now) ? formatTime(when) : formatDateTimeFi(when)}
              </span>
            </button>
            {open ? (
              <>
                <AnswerText text={body} />
                <div style={s.quickActions}>
                  <button style={s.secondaryBtn} onClick={() => onContinue(e)}>Jatka keskustelua</button>
                </div>
              </>
            ) : (
              body && <div style={s.answerPreview}>{stripMarkdown(body)}</div>
            )}
          </div>
        )
      })}
    </BottomSheet>
  )
}

function Row({ card, row, onOpen, onComplete }: {
  card: CardId
  row: HomeRow
  onOpen: () => void
  onComplete?: () => void
}) {
  const now = new Date()
  let leading: ComponentChildren = null
  let trailing: ComponentChildren = null

  if (card === 'today') {
    leading = (
      <button style={s.checkHit} aria-label="Merkitse tehdyksi" onClick={(e) => { e.stopPropagation(); onComplete?.() }}>
        <span style={s.checkCircle} />
      </button>
    )
    if (row.flag === 'overdue') trailing = <span style={{ ...s.trailing, color: AMBER }}>myöhässä</span>
    else if (row.when && hasClockTime(row.when)) trailing = <span style={s.trailing}>{formatTime(row.when)}</span>
  } else if (card === 'calendar' || card === 'reminders') {
    leading = <span style={s.timeCol}>{row.when ? formatTime(row.when) : ''}</span>
    if (row.flag === 'unanswered') trailing = <span style={s.amberPill}>Vastaamatta</span>
  } else if (row.when) {
    trailing = (
      <span style={s.trailing}>
        {sameLocalDay(row.when, now) ? formatTime(row.when) : row.when.toLocaleDateString('fi-FI', { day: 'numeric', month: 'numeric' })}
      </span>
    )
  }

  return (
    <div style={s.row}>
      {leading}
      <button style={{ ...s.rowTextBtn, paddingLeft: leading ? 0 : 12 }} onClick={onOpen} title={row.title}>
        <span style={s.rowTitle}>{row.title}</span>
        {trailing}
      </button>
    </div>
  )
}

// ── Input bar ────────────────────────────────────────────────────

type SpeechCtor = new () => {
  lang: string
  interimResults: boolean
  continuous: boolean
  start: () => void
  stop: () => void
  onresult: ((e: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null
  onend: (() => void) | null
  onerror: (() => void) | null
}

function getSpeechCtor(): SpeechCtor | null {
  const w = window as unknown as { SpeechRecognition?: SpeechCtor; webkitSpeechRecognition?: SpeechCtor }
  return w.SpeechRecognition || w.webkitSpeechRecognition || null
}

function InputBar({ draft, onDraft, onSubmit, busy, onChat, onCall }: {
  draft: string
  onDraft: (t: string) => void
  onSubmit: () => void
  busy: boolean
  onChat: () => void
  onCall: () => void
}) {
  const [listening, setListening] = useState(false)
  const recogRef = useRef<InstanceType<SpeechCtor> | null>(null)
  const Speech = useMemo(getSpeechCtor, [])
  const hasText = draft.trim().length > 0

  useEffect(() => () => recogRef.current?.stop(), [])

  // Dictation fills the field; it never sends. The user corrects a mis-heard
  // time before it becomes a commitment.
  const toggleDictation = () => {
    if (!Speech) return
    if (listening) { recogRef.current?.stop(); return }
    const r = new Speech()
    r.lang = 'fi-FI'
    r.interimResults = true
    r.continuous = false
    r.onresult = (e) => {
      let text = ''
      for (let i = 0; i < e.results.length; i++) text += e.results[i][0].transcript
      onDraft(text)
    }
    r.onend = () => setListening(false)
    r.onerror = () => setListening(false)
    recogRef.current = r
    r.start()
    setListening(true)
  }

  return (
    <div style={s.inputBar}>
      <div style={s.field}>
        <input
          style={s.input}
          value={draft}
          placeholder={listening ? 'Kuunnellaan…' : 'Kirjoita tai sanele…'}
          onInput={(e) => onDraft((e.target as HTMLInputElement).value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onSubmit() } }}
          {...{ enterkeyhint: 'send' }}
        />
        {/* Mic when empty, send when text — same slot, same size */}
        {hasText ? (
          <button style={{ ...s.fieldBtn, background: 'var(--primary)', color: '#fff', opacity: busy ? 0.5 : 1 }} disabled={busy} onClick={onSubmit} aria-label="Lähetä">
            <SendIcon />
          </button>
        ) : Speech ? (
          <button
            style={{ ...s.fieldBtn, color: listening ? '#fff' : 'var(--text-secondary)', background: listening ? 'var(--danger)' : 'transparent' }}
            onClick={toggleDictation}
            aria-label={listening ? 'Lopeta sanelu' : 'Sanele'}
          >
            <MicIcon />
          </button>
        ) : (
          <span style={{ ...s.fieldBtn, cursor: 'default' }} />
        )}
      </div>
      <ActionCircle label="Keskustele" color="var(--primary)" dim={hasText} onClick={onChat}><ChatIcon /></ActionCircle>
      <ActionCircle label="Soita" color="var(--success)" dim={hasText} onClick={onCall}><PhoneIcon /></ActionCircle>
    </div>
  )
}

function ActionCircle({ label, color, dim, onClick, children }: {
  label: string
  color: string
  dim: boolean
  onClick: () => void
  children: ComponentChildren
}) {
  return (
    <button style={{ ...s.actionHit, opacity: dim ? 0.45 : 1 }} onClick={onClick} aria-label={label}>
      <span style={{ ...s.actionCircle, background: color }}>{children}</span>
      <span style={s.actionLabel}>{label}</span>
    </button>
  )
}

// ── Bottom sheets ────────────────────────────────────────────────

function BottomSheet({ title, onClose, children }: { title: string; onClose: () => void; children: ComponentChildren }) {
  return (
    <div style={s.sheetOverlay} onClick={onClose}>
      <div style={s.sheet} onClick={(e) => e.stopPropagation()} role="dialog" aria-label={title}>
        <div style={s.sheetGrip} />
        <div style={{ display: 'flex', alignItems: 'center', padding: '0 16px 8px' }}>
          <div style={{ flex: 1, fontWeight: 600, fontSize: 17, minWidth: 0 }}>{title}</div>
          <button style={s.smallIconBtn} onClick={onClose} aria-label="Sulje">{'×'}</button>
        </div>
        <div style={{ padding: '0 16px 16px', overflow: 'auto' }}>{children}</div>
      </div>
    </div>
  )
}

function SheetHost({
  sheet, personas, selectedPersona, personaImages, onClose, onPersona, onComplete,
  onRescheduleTask, onRescheduleReminder, onCancelReminder, onNavigate, onCreate,
}: {
  sheet: Sheet
  personas: Persona[]
  selectedPersona: Persona
  personaImages: Record<string, string>
  onClose: () => void
  onPersona: (p: Persona) => void
  onComplete: (t: CloudTask) => void
  onRescheduleTask: (t: CloudTask, at: Date) => void
  onRescheduleReminder: (n: CloudNote, at: Date) => void
  onCancelReminder: (n: CloudNote) => void
  onNavigate: (view: string, focusId?: string) => void
  onCreate: (fn: () => Promise<unknown>, message: string) => void
}) {
  switch (sheet.kind) {
    case 'persona':
      return (
        <BottomSheet title="Valitse persoona" onClose={onClose}>
          {personas.map((p) => (
            <button
              key={p.id}
              style={{ ...s.personaRow, ...(p.id === selectedPersona.id ? { background: 'var(--system-bubble)' } : {}) }}
              onClick={() => onPersona(p)}
            >
              <Avatar persona={p} url={personaImages[p.id]} size={40} />
              <div style={{ textAlign: 'left', minWidth: 0 }}>
                <div style={s.personaName}>{p.name}</div>
                <div style={s.tagline}>{PERSONA_INFO[p.id]?.role || ''}</div>
              </div>
            </button>
          ))}
        </BottomSheet>
      )

    case 'task': {
      const { task } = sheet
      const when = taskWhen(task)
      return (
        <BottomSheet title={task.title} onClose={onClose}>
          <DetailLine label="Määräaika" value={when ? formatDateTimeFi(when, hasClockTime(when)) : 'Ei määräaikaa'} />
          {task.description && <DetailLine label="Kuvaus" value={task.description} />}
          <Reschedule initial={when} onSave={(at) => onRescheduleTask(task, at)} />
          <div style={s.sheetActions}>
            <button style={s.secondaryBtn} onClick={() => { onClose(); onNavigate('tasks', task.id) }}>Avaa tehtävät</button>
            <button style={s.primaryBtn} onClick={() => onComplete(task)}>Merkitse tehdyksi</button>
          </div>
        </BottomSheet>
      )
    }

    case 'event': {
      const { task } = sheet
      const when = taskWhen(task)
      return (
        <BottomSheet title={task.title} onClose={onClose}>
          <DetailLine label="Aika" value={when ? `${formatDateTimeFi(when)}${task.duration ? ` · ${task.duration} min` : ''}` : '—'} />
          {task.location && <DetailLine label="Paikka" value={task.location} />}
          {task.description && <DetailLine label="Kuvaus" value={task.description} />}
          <Reschedule initial={when} onSave={(at) => onRescheduleTask(task, at)} />
          <div style={s.sheetActions}>
            <button style={s.secondaryBtn} onClick={() => { onClose(); onNavigate('calendar') }}>Avaa kalenteri</button>
          </div>
        </BottomSheet>
      )
    }

    case 'reminder': {
      const { note } = sheet
      const when = reminderWhen(note)
      const meta = note.metadata as Record<string, unknown> | undefined
      const personaId = meta?.persona_id as string | undefined
      const caller = personas.find((p) => p.id === personaId)?.name
      return (
        <ReminderSheet
          note={note}
          when={when}
          caller={caller}
          onClose={onClose}
          onReschedule={(at) => onRescheduleReminder(note, at)}
          onCancel={() => onCancelReminder(note)}
          onOpenAll={() => { onClose(); onNavigate(note.type === 5 ? 'alerts' : 'calls', note.id) }}
        />
      )
    }

    case 'newTask':
      return <NewTaskSheet initial={sheet.draft} onClose={onClose} onCreate={onCreate} />
    case 'newEvent':
      return <NewEventSheet initial={sheet.draft} onClose={onClose} onCreate={onCreate} />
    case 'newReminder':
      return <NewReminderSheet initial={sheet.draft} personas={personas} onClose={onClose} onCreate={onCreate} />
    case 'quickList':
      return null // rendered by HomeScreen, which owns the quick chat list
    case 'newNote':
      return <NewNoteSheet initial={sheet.draft} onClose={onClose} onCreate={onCreate} />
  }
}

function ReminderSheet({ note, when, caller, onClose, onReschedule, onCancel, onOpenAll }: {
  note: CloudNote
  when: Date | null
  caller?: string
  onClose: () => void
  onReschedule: (at: Date) => void
  onCancel: () => void
  onOpenAll: () => void
}) {
  const [moving, setMoving] = useState(false)
  return (
    <BottomSheet title={note.title || 'Muistutus'} onClose={onClose}>
      <DetailLine label="Aika" value={when ? formatDateTimeFi(when) : '—'} />
      {caller && <DetailLine label="Kuka soittaa" value={caller} />}
      {moving && <Reschedule initial={when} onSave={onReschedule} open />}
      <div style={s.sheetActions}>
        <button style={s.secondaryBtn} onClick={() => setMoving((m) => !m)}>Siirrä</button>
        <button style={s.secondaryBtn} onClick={onCancel}>Peruuta</button>
        <button style={{ ...s.primaryBtn, background: 'var(--success)', opacity: 0.5, cursor: 'not-allowed' }} disabled title="Puhelut toimivat mobiilisovelluksessa">
          Soita nyt
        </button>
      </div>
      <div style={{ textAlign: 'center', marginTop: 8 }}>
        <button style={s.textBtn} onClick={onOpenAll}>Näytä kaikki</button>
      </div>
    </BottomSheet>
  )
}

function Reschedule({ initial, onSave, open = false }: { initial: Date | null; onSave: (at: Date) => void; open?: boolean }) {
  const base = initial || nextRoundHour()
  const [expanded, setExpanded] = useState(open)
  const [date, setDate] = useState(toDateInput(base))
  const [time, setTime] = useState(initial && !hasClockTime(initial) ? '' : toTimeInput(base))
  if (!expanded) {
    return <button style={{ ...s.textBtn, padding: '8px 0' }} onClick={() => setExpanded(true)}>Siirrä toiseen aikaan…</button>
  }
  const at = fromInputs(date, time)
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', margin: '8px 0' }}>
      <div style={{ flex: 1 }}><FormField label="Päivä" type="date" value={date} onChange={setDate} /></div>
      <div style={{ width: 110 }}><FormField label="Kello" type="time" value={time} onChange={setTime} /></div>
      <button style={{ ...s.primaryBtn, marginBottom: 12 }} disabled={!at} onClick={() => at && onSave(at)}>Tallenna</button>
    </div>
  )
}

function NewTaskSheet({ initial, onClose, onCreate }: { initial: TaskDraft; onClose: () => void; onCreate: (fn: () => Promise<unknown>, message: string) => void }) {
  const [d, setD] = useState(initial)
  return (
    <BottomSheet title="Uusi tehtävä" onClose={onClose}>
      <FormField label="Tehtävä" value={d.title} onChange={(title) => setD({ ...d, title })} required />
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1 }}><FormField label="Päivä" type="date" value={d.date} onChange={(date) => setD({ ...d, date })} /></div>
        <div style={{ width: 110 }}><FormField label="Kello (valinnainen)" type="time" value={d.time} onChange={(time) => setD({ ...d, time })} /></div>
      </div>
      <div style={s.sheetActions}>
        <button style={s.primaryBtn} disabled={!d.title.trim()} onClick={() => onCreate(() => createTask(d), `Tehtävä lisätty: ${d.title.trim()}`)}>Tallenna</button>
      </div>
    </BottomSheet>
  )
}

function NewEventSheet({ initial, onClose, onCreate }: { initial: EventDraft; onClose: () => void; onCreate: (fn: () => Promise<unknown>, message: string) => void }) {
  const [d, setD] = useState(initial)
  return (
    <BottomSheet title="Uusi tapahtuma" onClose={onClose}>
      <FormField label="Otsikko" value={d.title} onChange={(title) => setD({ ...d, title })} required />
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1 }}><FormField label="Päivä" type="date" value={d.date} onChange={(date) => setD({ ...d, date })} /></div>
        <div style={{ width: 110 }}><FormField label="Kello" type="time" value={d.time} onChange={(time) => setD({ ...d, time })} /></div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ width: 110 }}><FormField label="Kesto (min)" type="number" value={String(d.durationMin)} onChange={(v) => setD({ ...d, durationMin: Number(v) || 60 })} /></div>
        <div style={{ flex: 1 }}><FormField label="Paikka" value={d.location} onChange={(location) => setD({ ...d, location })} /></div>
      </div>
      <div style={s.sheetActions}>
        <button style={s.primaryBtn} disabled={!d.title.trim() || !d.time} onClick={() => onCreate(() => createEvent(d), `Tapahtuma lisätty: ${d.title.trim()}`)}>Tallenna</button>
      </div>
    </BottomSheet>
  )
}

function NewReminderSheet({ initial, personas, onClose, onCreate }: {
  initial: ReminderDraft
  personas: Persona[]
  onClose: () => void
  onCreate: (fn: () => Promise<unknown>, message: string) => void
}) {
  const [d, setD] = useState(initial)
  const now = new Date()
  const inOneHour = new Date(now.getTime() + 60 * 60 * 1000)
  const tonight = new Date(now); tonight.setHours(18, 0, 0, 0)
  const tomorrowMorning = new Date(now); tomorrowMorning.setDate(now.getDate() + 1); tomorrowMorning.setHours(9, 0, 0, 0)
  const presets: Array<{ label: string; at: Date }> = [
    { label: '1 h päästä', at: inOneHour },
    ...(tonight > inOneHour ? [{ label: 'Tänään 18.00', at: tonight }] : []),
    { label: 'Huomenna 9.00', at: tomorrowMorning },
  ]
  const title = d.title.trim() || 'Soita minulle'
  const save = (at: Date) => onCreate(
    () => createReminder({ ...d, title, date: toDateInput(at), time: toTimeInput(at) }),
    `Soitto ajastettu: ${formatDateTimeFi(at)}`,
  )
  const custom = fromInputs(d.date, d.time)
  return (
    <BottomSheet title="Ajasta soitto" onClose={onClose}>
      {/* One tap on a preset saves — scheduling a call in two taps without typing. */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        {presets.map((p) => (
          <button key={p.label} style={s.chip} onClick={() => save(p.at)}>{p.label}</button>
        ))}
      </div>
      <FormField label="Aihe" value={d.title} onChange={(t) => setD({ ...d, title: t })} placeholder="Soita minulle" />
      <FormField
        label="Kuka soittaa"
        type="select"
        value={d.personaId}
        onChange={(personaId) => setD({ ...d, personaId })}
        options={personas.map((p) => ({ value: p.id, label: p.name }))}
      />
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1 }}><FormField label="Päivä" type="date" value={d.date} onChange={(date) => setD({ ...d, date })} /></div>
        <div style={{ width: 110 }}><FormField label="Kello" type="time" value={d.time} onChange={(time) => setD({ ...d, time })} /></div>
      </div>
      <div style={s.sheetActions}>
        <button style={s.primaryBtn} disabled={!custom} onClick={() => custom && save(custom)}>Tallenna</button>
      </div>
    </BottomSheet>
  )
}

function NewNoteSheet({ initial, onClose, onCreate }: { initial: NoteDraft; onClose: () => void; onCreate: (fn: () => Promise<unknown>, message: string) => void }) {
  const [d, setD] = useState(initial)
  return (
    <BottomSheet title="Uusi muistiinpano" onClose={onClose}>
      <FormField label="Otsikko" value={d.title} onChange={(title) => setD({ ...d, title })} required />
      <FormField label="Sisältö" type="textarea" value={d.content} onChange={(content) => setD({ ...d, content })} />
      <div style={s.sheetActions}>
        <button style={s.primaryBtn} disabled={!d.title.trim()} onClick={() => onCreate(() => createNote(d), `Muistiinpano tallennettu: ${d.title.trim()}`)}>Tallenna</button>
      </div>
    </BottomSheet>
  )
}

function DetailLine({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 15, color: 'var(--text)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{value}</div>
    </div>
  )
}

// ── Small pieces ─────────────────────────────────────────────────

function Avatar({ persona, url, size }: { persona: Persona; url?: string; size: number }) {
  const [failed, setFailed] = useState(false)
  const box = { width: size, height: size, borderRadius: size / 2, flexShrink: 0, overflow: 'hidden', background: 'var(--system-bubble)', display: 'flex', alignItems: 'center', justifyContent: 'center' }
  if (url && !failed) {
    return <img src={url} alt="" style={{ ...box, objectFit: 'cover' }} onError={() => setFailed(true)} />
  }
  return <span style={{ ...box, fontSize: size * 0.55 }}>{PERSONA_INFO[persona.id]?.emoji || persona.emoji}</span>
}

function IconButton({ label, onClick, children }: { label: string; onClick: () => void; children: ComponentChildren }) {
  return <button style={s.iconBtn} onClick={onClick} aria-label={label} title={label}>{children}</button>
}

const svgProps = { width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': 2, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' } as const

const ChevronIcon = () => <svg {...svgProps} width={18} height={18}><path d="m6 9 6 6 6-6" /></svg>
const MailIcon = () => <svg {...svgProps}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>
const GearIcon = () => <svg {...svgProps}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" /></svg>
const MicIcon = () => <svg {...svgProps} width={20} height={20}><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3" /></svg>
const SendIcon = () => <svg {...svgProps} width={18} height={18}><path d="M12 19V5M5 12l7-7 7 7" /></svg>
const ChatIcon = () => <svg {...svgProps} width={20} height={20} stroke="#fff"><path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" /></svg>
const PhoneIcon = () => <svg {...svgProps} width={20} height={20} stroke="#fff"><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z" /></svg>
const NoteIcon = () => <svg {...svgProps}><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><path d="M14 3v6h6M8 13h8M8 17h5" /></svg>
const SparkIcon = () => <svg {...svgProps}><path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9zM19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8z" /></svg>

// ── Styles ───────────────────────────────────────────────────────

const s: Record<string, Record<string, string | number>> = {
  outer: { height: '100%', background: 'var(--bg)', display: 'flex', justifyContent: 'center', overflow: 'hidden' },
  // Wider than a phone on desktop, but never scaled: the type stays at its
  // designed size and the extra width goes to the content.
  column: { position: 'relative', width: '100%', maxWidth: 760, flexShrink: 0, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  header: { display: 'flex', alignItems: 'center', gap: 4, padding: '12px 12px 8px 8px', flexShrink: 0 },
  personaBtn: { display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, minHeight: 44, padding: '4px 8px', background: 'transparent', border: 'none', borderRadius: 12, color: 'var(--text)', cursor: 'pointer' },
  personaName: { fontSize: 16, fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  tagline: { fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  iconBtn: { width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', borderRadius: 22, color: 'var(--text-secondary)', cursor: 'pointer' },
  scroll: { flex: 1, overflowY: 'auto', padding: '4px 16px 8px' },
  card: { background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 14, marginBottom: 10, overflow: 'hidden' },
  cardHeader: { display: 'flex', alignItems: 'center', minHeight: 48, paddingRight: 4 },
  cardTitleBtn: { flex: 1, display: 'flex', alignItems: 'center', gap: 8, minHeight: 48, padding: '0 14px', background: 'transparent', border: 'none', color: 'var(--text)', cursor: 'pointer', textAlign: 'left' },
  cardTitle: { fontSize: 15, fontWeight: 600 },
  count: { fontSize: 12, fontWeight: 600, minWidth: 20, padding: '1px 7px', borderRadius: 10, background: 'var(--system-bubble)', color: 'var(--text-secondary)', textAlign: 'center' },
  smallIconBtn: { width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', color: 'var(--text-secondary)', fontSize: 22, cursor: 'pointer', flexShrink: 0 },
  row: { display: 'flex', alignItems: 'center', minHeight: 44, borderTop: '1px solid var(--border)' },
  rowTextBtn: { flex: 1, minWidth: 0, minHeight: 44, display: 'flex', alignItems: 'center', gap: 8, paddingRight: 14, background: 'transparent', border: 'none', color: 'var(--text)', cursor: 'pointer', textAlign: 'left', font: 'inherit' },
  rowTitle: { flex: 1, minWidth: 0, fontSize: 15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  trailing: { fontSize: 13, color: 'var(--text-muted)', flexShrink: 0 },
  timeCol: { width: 56, paddingLeft: 14, fontSize: 13, color: 'var(--text-secondary)', flexShrink: 0, fontVariantNumeric: 'tabular-nums' },
  checkHit: { width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', cursor: 'pointer', flexShrink: 0 },
  checkCircle: { width: 20, height: 20, borderRadius: 10, border: '2px solid var(--text-muted)' },
  amberPill: { fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, color: AMBER, background: 'rgba(245, 166, 35, 0.14)', flexShrink: 0 },
  destinations: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, margin: '6px 0 8px' },
  destBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, minHeight: 52, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 14, color: 'var(--text)', fontSize: 15, fontWeight: 500, cursor: 'pointer' },
  quickCard: { padding: '12px 14px', borderColor: 'var(--primary)' },
  quickLabel: { fontSize: 12, fontWeight: 600, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: 0.4 },
  quickText: { fontSize: 13, color: 'var(--text-muted)', marginTop: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  quickActions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 },
  dialogOverlay: { position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 12 },
  dialog: { width: '100%', maxWidth: 620, maxHeight: '80%', display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)', border: '1px solid var(--primary)', borderRadius: 16, padding: '8px 16px 14px', boxShadow: '0 12px 40px rgba(0,0,0,0.45)' },
  dialogQuestion: { fontSize: 17, fontWeight: 600, color: 'var(--text)', margin: '0 0 4px', wordBreak: 'break-word' },
  answerText: { fontSize: 15, lineHeight: 1.5, color: 'var(--text)', wordBreak: 'break-word', marginTop: 8 },
  answerPreview: { fontSize: 13, lineHeight: 1.4, color: 'var(--text-muted)', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', marginBottom: 4 },
  quickItem: { borderTop: '1px solid var(--border)', padding: '2px 0 10px' },
  quickItemHead: { width: '100%', display: 'flex', alignItems: 'center', gap: 8, minHeight: 44, padding: 0, background: 'transparent', border: 'none', color: 'var(--text)', cursor: 'pointer', textAlign: 'left', font: 'inherit' },
  confirmTitle: { fontSize: 17, fontWeight: 600, marginTop: 2, wordBreak: 'break-word' },
  confirmWhen: { fontSize: 15, color: 'var(--text-secondary)', marginTop: 2 },
  primaryBtn: { minHeight: 44, padding: '0 18px', background: 'var(--primary)', border: 'none', borderRadius: 22, color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer' },
  secondaryBtn: { minHeight: 44, padding: '0 16px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 22, color: 'var(--text)', fontSize: 15, cursor: 'pointer' },
  textBtn: { minHeight: 36, padding: '0 8px', background: 'transparent', border: 'none', color: 'var(--text-muted)', fontSize: 13, cursor: 'pointer' },
  chip: { minHeight: 44, padding: '0 16px', background: 'var(--system-bubble)', border: '1px solid var(--primary)', borderRadius: 22, color: 'var(--text)', fontSize: 14, cursor: 'pointer' },
  snack: { position: 'absolute', left: 16, right: 16, bottom: 96, display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px 6px 14px', minHeight: 44, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, color: 'var(--text)', fontSize: 14, boxShadow: '0 6px 20px rgba(0,0,0,0.35)', zIndex: 5 },
  inputBar: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px 12px 16px', borderTop: '1px solid var(--border)', background: 'var(--bg)', flexShrink: 0 },
  field: { flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', minHeight: 48, padding: '0 4px 0 14px', background: 'var(--user-bubble)', border: '1px solid var(--border)', borderRadius: 24 },
  input: { flex: 1, minWidth: 0, height: 44, background: 'transparent', border: 'none', outline: 'none', color: 'var(--text)', fontSize: 16 },
  fieldBtn: { width: 38, height: 38, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', borderRadius: 19, cursor: 'pointer' },
  actionHit: { width: 60, minHeight: 56, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, background: 'transparent', border: 'none', cursor: 'pointer', flexShrink: 0, padding: 0, transition: 'opacity 0.15s' },
  actionCircle: { width: 38, height: 38, borderRadius: 19, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  actionLabel: { fontSize: 11, color: 'var(--text-secondary)' },
  sheetOverlay: { position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', zIndex: 10 },
  sheet: { maxHeight: '85%', display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)', borderTop: '1px solid var(--border)', borderRadius: '18px 18px 0 0', paddingTop: 8 },
  sheetGrip: { width: 40, height: 4, borderRadius: 2, background: 'var(--border)', margin: '0 auto 10px' },
  sheetActions: { display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 8, marginTop: 12 },
  personaRow: { width: '100%', display: 'flex', alignItems: 'center', gap: 12, minHeight: 56, padding: '6px 8px', background: 'transparent', border: 'none', borderRadius: 12, color: 'var(--text)', cursor: 'pointer' },
}
